import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Bot,
  CheckCircle2,
  Inbox,
  PhoneMissed,
  Phone,
  Send,
  Voicemail,
  MessageSquareText,
} from "lucide-react";
import { fetchReportsData } from "./reportsApi";
import type { ReportsPayload } from "./reportsApi";
import { tagCall, type CallTag } from "../voice/tagCall";
import { buildSmsIndex } from "../voice/buildSmsIndex";
import type { SmsIndexEntry } from "../voice/tagCall";

// Phase 2.5 — Reports. Admin-only (the route is only exposed on the
// Launcher / Sidebar when `hq_modules.reports` is in me.modules, which
// for non-admins is never true — see 0007_reports_module.sql).
//
// Taxonomy:
//   * Call counts use `tagCall` — exactly the helper /voice/calls uses —
//     so "Answered / Left a VM / Missed / In Progress" match pill counts
//     between the two surfaces to the row. No separate SQL.
//   * "Sent a Text" is the composite count: calls with a same-number
//     outbound SMS within ±60 min, derived from the same SmsIndex
//     CallsView now uses. When the operator sees 4 "Sent a Text" badges
//     in /voice/calls, Reports says 4. Period.
//   * SMS In / SMS Out are direction counts on /sms/messages for the
//     same window — zero schema coupling to Voice.

// ── Period presets (lifted from the legacy ReportsView) ─────────────

type PeriodKey = "this-week" | "last-7" | "this-month" | "last-30" | "custom";

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: "this-week", label: "This Week" },
  { key: "last-7", label: "Last 7 Days" },
  { key: "this-month", label: "This Month" },
  { key: "last-30", label: "Last 30 Days" },
  { key: "custom", label: "Custom" },
];

function localDateStub(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function toIsoStart(stub: string): string {
  // Local-midnight inclusive lower bound, same convention as
  // /voice/calls — so "from April 1" means "on and after April 1 local".
  return new Date(`${stub}T00:00:00`).toISOString();
}

function toIsoEndExclusive(stub: string): string {
  // Exclusive upper bound — "to April 22" includes all of April 22.
  const d = new Date(`${stub}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

function rangeForPeriod(
  period: PeriodKey,
  customStart: string,
  customEnd: string
): { fromIso: string; toIso: string; fromStub: string; toStub: string } {
  const now = new Date();
  const todayStub = localDateStub(now);

  switch (period) {
    case "this-week": {
      const d = new Date(now);
      const day = d.getDay();
      // Week starts Monday, matching the legacy ReportsView.
      const diff = day === 0 ? 6 : day - 1;
      d.setDate(d.getDate() - diff);
      const fromStub = localDateStub(d);
      return {
        fromStub,
        toStub: todayStub,
        fromIso: toIsoStart(fromStub),
        toIso: toIsoEndExclusive(todayStub),
      };
    }
    case "last-7": {
      const d = new Date(now);
      d.setDate(d.getDate() - 6); // inclusive 7-day window (today + prior 6)
      const fromStub = localDateStub(d);
      return {
        fromStub,
        toStub: todayStub,
        fromIso: toIsoStart(fromStub),
        toIso: toIsoEndExclusive(todayStub),
      };
    }
    case "this-month": {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      const fromStub = localDateStub(d);
      return {
        fromStub,
        toStub: todayStub,
        fromIso: toIsoStart(fromStub),
        toIso: toIsoEndExclusive(todayStub),
      };
    }
    case "last-30": {
      const d = new Date(now);
      d.setDate(d.getDate() - 29); // inclusive 30-day window
      const fromStub = localDateStub(d);
      return {
        fromStub,
        toStub: todayStub,
        fromIso: toIsoStart(fromStub),
        toIso: toIsoEndExclusive(todayStub),
      };
    }
    case "custom": {
      const fromStub =
        customStart ||
        localDateStub(new Date(Date.now() - 7 * 86400000));
      const toStub = customEnd || todayStub;
      return {
        fromStub,
        toStub,
        fromIso: toIsoStart(fromStub),
        toIso: toIsoEndExclusive(toStub),
      };
    }
  }
}

function toDateKey(isoString: string): string {
  const d = new Date(isoString);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split("-");
  const date = new Date(+y, +m - 1, +d);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

// ── Derived summaries ──────────────────────────────────────────────

type Summary = {
  totalCalls: number;
  answered: number;
  aiAnswered: number;
  voicemails: number;
  missed: number;
  inProgress: number;
  sentText: number;
  smsInbound: number;
  smsOutbound: number;
};

type DailyBucket = {
  date: string;
  answered: number;
  aiAnswered: number;
  voicemails: number;
  missed: number;
  inProgress: number;
  smsInbound: number;
  smsOutbound: number;
};

function emptySummary(): Summary {
  return {
    totalCalls: 0,
    answered: 0,
    aiAnswered: 0,
    voicemails: 0,
    missed: 0,
    inProgress: 0,
    sentText: 0,
    smsInbound: 0,
    smsOutbound: 0,
  };
}

function emptyBucket(date: string): DailyBucket {
  return {
    date,
    answered: 0,
    aiAnswered: 0,
    voicemails: 0,
    missed: 0,
    inProgress: 0,
    smsInbound: 0,
    smsOutbound: 0,
  };
}

function computeSummary(
  payload: ReportsPayload,
  smsIndex: readonly SmsIndexEntry[]
): { summary: Summary; daily: DailyBucket[] } {
  const summary = emptySummary();
  const dayMap: Record<string, DailyBucket> = {};

  for (const call of payload.calls) {
    const tagged = tagCall(call, smsIndex);
    summary.totalCalls += 1;
    bumpTag(summary, tagged.tag);
    if (tagged.sentText) summary.sentText += 1;

    const dk = toDateKey(call.created_at);
    const bucket = dayMap[dk] ?? (dayMap[dk] = emptyBucket(dk));
    bumpBucketTag(bucket, tagged.tag);
  }

  for (const msg of payload.messages) {
    if (msg.direction === "inbound") summary.smsInbound += 1;
    else summary.smsOutbound += 1;

    const dk = toDateKey(msg.created_at);
    const bucket = dayMap[dk] ?? (dayMap[dk] = emptyBucket(dk));
    if (msg.direction === "inbound") bucket.smsInbound += 1;
    else bucket.smsOutbound += 1;
  }

  const daily = Object.values(dayMap).sort((a, b) => b.date.localeCompare(a.date));
  return { summary, daily };
}

function bumpTag(s: Summary, tag: CallTag): void {
  switch (tag) {
    case "answered":
      s.answered += 1;
      break;
    case "ai_answered":
      s.aiAnswered += 1;
      break;
    case "left_vm":
      s.voicemails += 1;
      break;
    case "missed":
      s.missed += 1;
      break;
    case "in_progress":
      s.inProgress += 1;
      break;
    case "unknown":
      break;
  }
}

function bumpBucketTag(b: DailyBucket, tag: CallTag): void {
  switch (tag) {
    case "answered":
      b.answered += 1;
      break;
    case "ai_answered":
      b.aiAnswered += 1;
      break;
    case "left_vm":
      b.voicemails += 1;
      break;
    case "missed":
      b.missed += 1;
      break;
    case "in_progress":
      b.inProgress += 1;
      break;
    case "unknown":
      break;
  }
}

// ── Summary card definitions ───────────────────────────────────────

type CardKey = keyof Summary;

type CardDef = {
  key: CardKey;
  label: string;
  Icon: typeof Phone;
  accent: string; // left-border + icon tint
};

const CARDS: CardDef[] = [
  { key: "totalCalls", label: "Total Calls", Icon: Phone, accent: "border-gray-400 text-gray-300" },
  { key: "answered", label: "Answered", Icon: CheckCircle2, accent: "border-green-500 text-green-400" },
  { key: "aiAnswered", label: "AI Answered", Icon: Bot, accent: "border-emerald-500 text-emerald-400" },
  { key: "voicemails", label: "Voicemails", Icon: Voicemail, accent: "border-red-500 text-red-400" },
  { key: "missed", label: "Missed", Icon: PhoneMissed, accent: "border-yellow-500 text-yellow-400" },
  { key: "sentText", label: "Sent a Text", Icon: MessageSquareText, accent: "border-blue-400 text-blue-300" },
  { key: "smsInbound", label: "SMS Received", Icon: Inbox, accent: "border-blue-500 text-blue-400" },
  { key: "smsOutbound", label: "SMS Sent", Icon: Send, accent: "border-emerald-500 text-emerald-400" },
];

// ── CSV export ─────────────────────────────────────────────────────

function csvEscape(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(
  summary: Summary,
  daily: DailyBucket[],
  fromStub: string,
  toStub: string
): void {
  const header = [
    "date",
    "total_calls",
    "answered",
    "ai_answered",
    "voicemails",
    "missed",
    "in_progress",
    "sms_inbound",
    "sms_outbound",
  ];
  const rows: string[] = [header.join(",")];
  for (const d of daily) {
    const total = d.answered + d.aiAnswered + d.voicemails + d.missed + d.inProgress;
    rows.push(
      [d.date, total, d.answered, d.aiAnswered, d.voicemails, d.missed, d.inProgress, d.smsInbound, d.smsOutbound]
        .map(csvEscape)
        .join(",")
    );
  }
  rows.push(
    [
      "TOTAL",
      summary.totalCalls,
      summary.answered,
      summary.aiAnswered,
      summary.voicemails,
      summary.missed,
      summary.inProgress,
      summary.smsInbound,
      summary.smsOutbound,
    ]
      .map(csvEscape)
      .join(",")
  );

  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = `tribal-axe-reports-${fromStub}_to_${toStub}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

// ── View ───────────────────────────────────────────────────────────

const BAR_COLORS: Record<keyof Omit<DailyBucket, "date">, string> = {
  answered: "bg-green-500",
  aiAnswered: "bg-emerald-400",
  voicemails: "bg-red-500",
  missed: "bg-yellow-500",
  inProgress: "bg-yellow-700",
  smsInbound: "bg-blue-500",
  smsOutbound: "bg-emerald-500",
};

const LEGEND: { label: string; color: string }[] = [
  { label: "Answered", color: "bg-green-500" },
  { label: "AI Answered", color: "bg-emerald-400" },
  { label: "Voicemails", color: "bg-red-500" },
  { label: "Missed", color: "bg-yellow-500" },
  { label: "In Progress", color: "bg-yellow-700" },
  { label: "SMS In", color: "bg-blue-500" },
  { label: "SMS Out", color: "bg-emerald-500" },
];

export function ReportsModule() {
  const [period, setPeriod] = useState<PeriodKey>("this-week");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [payload, setPayload] = useState<ReportsPayload | null>(null);
  const [smsIndex, setSmsIndex] = useState<SmsIndexEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const range = useMemo(
    () => rangeForPeriod(period, customStart, customEnd),
    [period, customStart, customEnd]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [data, index] = await Promise.all([
        fetchReportsData({ fromIso: range.fromIso, toIso: range.toIso }),
        buildSmsIndex({ fromIso: range.fromIso, toIso: range.toIso }),
      ]);
      setPayload(data);
      setSmsIndex(index);
    } catch (err) {
      setLoadError((err as Error).message);
      setPayload(null);
      setSmsIndex([]);
    } finally {
      setLoading(false);
    }
  }, [range.fromIso, range.toIso]);

  useEffect(() => {
    load();
  }, [load]);

  const { summary, daily } = useMemo(() => {
    if (!payload) return { summary: emptySummary(), daily: [] as DailyBucket[] };
    return computeSummary(payload, smsIndex);
  }, [payload, smsIndex]);

  const maxActivity = useMemo(
    () =>
      Math.max(
        1,
        ...daily.map(
          (d) =>
            d.answered +
            d.aiAnswered +
            d.voicemails +
            d.missed +
            d.inProgress +
            d.smsInbound +
            d.smsOutbound
        )
      ),
    [daily]
  );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-3 border-b border-tribal-border bg-tribal-surface px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-tribal-red" aria-hidden />
          <h2 className="text-base font-semibold">Reports</h2>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriod(p.key)}
              className={`rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
                period === p.key
                  ? "bg-tribal-red text-white"
                  : "bg-tribal-border text-tribal-muted hover:text-white"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {period === "custom" && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStart}
              max={customEnd || undefined}
              onChange={(e) => setCustomStart(e.target.value)}
              className="rounded-md border border-tribal-border bg-black/30 px-2 py-1 text-xs text-white focus:border-tribal-red focus:outline-none"
            />
            <span className="text-xs text-tribal-muted">to</span>
            <input
              type="date"
              value={customEnd}
              min={customStart || undefined}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="rounded-md border border-tribal-border bg-black/30 px-2 py-1 text-xs text-white focus:border-tribal-red focus:outline-none"
            />
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {loadError && <span className="text-xs text-red-400">{loadError}</span>}
          <button
            type="button"
            onClick={() => downloadCsv(summary, daily, range.fromStub, range.toStub)}
            disabled={daily.length === 0}
            className="rounded-md bg-white/5 px-3 py-1 text-xs text-tribal-muted transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
            title="Download current window as CSV"
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => load()}
            className="rounded-md bg-white/5 px-3 py-1 text-xs text-tribal-muted transition-colors hover:bg-white/10 hover:text-white"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-tribal-muted">
            Loading report data…
          </div>
        ) : (
          <div className="space-y-6">
            {payload?.truncated && (
              <div className="rounded-lg border border-yellow-600/40 bg-yellow-600/10 px-3 py-2 text-xs text-yellow-300">
                This window exceeded the 2 000-row fetch cap. Numbers may be
                under the true totals — narrow the date range or page the
                detail view via <code>/voice/calls</code> and
                <code> /sms/messages</code>.
              </div>
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
              {CARDS.map((card) => {
                const Icon = card.Icon;
                return (
                  <div
                    key={card.key}
                    className={`rounded-lg border-l-4 bg-tribal-surface p-4 ${card.accent}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" aria-hidden />
                      <span className="text-xs font-medium text-tribal-muted">
                        {card.label}
                      </span>
                    </div>
                    <div className="mt-1 text-3xl font-semibold text-white">
                      {summary[card.key]}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Daily trend */}
            {daily.length > 0 && (
              <div className="rounded-lg bg-tribal-surface p-4">
                <h3 className="mb-4 text-sm font-medium text-tribal-muted">
                  Daily Activity
                </h3>
                <div className="space-y-2">
                  {daily.map((d) => {
                    const total =
                      d.answered +
                      d.aiAnswered +
                      d.voicemails +
                      d.missed +
                      d.inProgress +
                      d.smsInbound +
                      d.smsOutbound;
                    return (
                      <div key={d.date} className="flex items-center gap-3">
                        <span className="w-28 flex-shrink-0 text-right text-xs text-tribal-muted">
                          {formatDateLabel(d.date)}
                        </span>
                        <div className="flex h-5 flex-1 overflow-hidden rounded bg-black/30">
                          {(
                            [
                              "answered",
                              "aiAnswered",
                              "voicemails",
                              "missed",
                              "inProgress",
                              "smsInbound",
                              "smsOutbound",
                            ] as (keyof Omit<DailyBucket, "date">)[]
                          ).map((metric) =>
                            d[metric] > 0 ? (
                              <div
                                key={metric}
                                className={`${BAR_COLORS[metric]} transition-all`}
                                style={{ width: `${(d[metric] / maxActivity) * 100}%` }}
                                title={`${metric}: ${d[metric]}`}
                              />
                            ) : null
                          )}
                        </div>
                        <span className="w-10 flex-shrink-0 text-right text-xs text-tribal-muted">
                          {total}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 flex flex-wrap gap-4 border-t border-tribal-border pt-3">
                  {LEGEND.map((item) => (
                    <div key={item.label} className="flex items-center gap-1.5">
                      <div className={`h-2.5 w-2.5 rounded-sm ${item.color}`} />
                      <span className="text-xs text-tribal-muted">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Daily table */}
            <div className="overflow-hidden rounded-lg bg-tribal-surface">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-tribal-border text-xs uppercase tracking-wider text-tribal-muted">
                      <th className="px-4 py-3 text-left font-medium">Date</th>
                      <th className="px-4 py-3 text-right font-medium">Calls</th>
                      <th className="px-4 py-3 text-right font-medium">Answered</th>
                      <th className="px-4 py-3 text-right font-medium">AI Answered</th>
                      <th className="px-4 py-3 text-right font-medium">Voicemails</th>
                      <th className="px-4 py-3 text-right font-medium">Missed</th>
                      <th className="px-4 py-3 text-right font-medium">In Prog.</th>
                      <th className="px-4 py-3 text-right font-medium">SMS In</th>
                      <th className="px-4 py-3 text-right font-medium">SMS Out</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-tribal-border/60">
                    {daily.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-tribal-muted">
                          No activity in this period
                        </td>
                      </tr>
                    ) : (
                      <>
                        {daily.map((d) => {
                          const totalCalls =
                            d.answered + d.aiAnswered + d.voicemails + d.missed + d.inProgress;
                          return (
                            <tr
                              key={d.date}
                              className="transition-colors hover:bg-white/[0.02]"
                            >
                              <td className="px-4 py-2 text-gray-300">
                                {formatDateLabel(d.date)}
                              </td>
                              <td className="px-4 py-2 text-right font-medium text-white">
                                {totalCalls}
                              </td>
                              <td className="px-4 py-2 text-right text-green-400">
                                {d.answered}
                              </td>
                              <td className="px-4 py-2 text-right text-emerald-400">
                                {d.aiAnswered}
                              </td>
                              <td className="px-4 py-2 text-right text-red-400">
                                {d.voicemails}
                              </td>
                              <td className="px-4 py-2 text-right text-yellow-400">
                                {d.missed}
                              </td>
                              <td className="px-4 py-2 text-right text-yellow-600">
                                {d.inProgress}
                              </td>
                              <td className="px-4 py-2 text-right text-blue-400">
                                {d.smsInbound}
                              </td>
                              <td className="px-4 py-2 text-right text-emerald-400">
                                {d.smsOutbound}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-black/20 font-medium">
                          <td className="px-4 py-2 text-white">Total</td>
                          <td className="px-4 py-2 text-right text-white">
                            {summary.totalCalls}
                          </td>
                          <td className="px-4 py-2 text-right text-green-400">
                            {summary.answered}
                          </td>
                          <td className="px-4 py-2 text-right text-emerald-400">
                            {summary.aiAnswered}
                          </td>
                          <td className="px-4 py-2 text-right text-red-400">
                            {summary.voicemails}
                          </td>
                          <td className="px-4 py-2 text-right text-yellow-400">
                            {summary.missed}
                          </td>
                          <td className="px-4 py-2 text-right text-yellow-600">
                            {summary.inProgress}
                          </td>
                          <td className="px-4 py-2 text-right text-blue-400">
                            {summary.smsInbound}
                          </td>
                          <td className="px-4 py-2 text-right text-emerald-400">
                            {summary.smsOutbound}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-[10px] text-tribal-muted">
              Call dispositions computed with the same <code>tagCall</code>{" "}
              helper that powers <code>/voice/calls</code>. "Sent a Text" counts
              calls where staff texted the caller within ±60 min from the same
              line. SMS counts exclude hidden / archived messages.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
